import { useQuery } from "@tanstack/react-query";
import { ArrowLeft } from "lucide-react";
import { useState } from "react";
import type { AuditEntry } from "@estelle/domain";

import { useDataSource } from "@/adapters/data-source";
import { LoadFailed, NoMatches } from "@/components/shell/EmptyState";
import { TableSkeleton } from "@/components/shell/LoadingState";
import { renderCell } from "@/components/table/cells";
import { DataTable } from "@/components/table/DataTable";
import { Pagination } from "@/components/table/Pagination";
import { useCursorHistory } from "@/components/table/useCursorHistory";
import { Button } from "@/components/ui/button";
import { auditActionLabel } from "@/modules/settings/fields";
import { projectAuditEntry } from "@/modules/settings/projections";
import type { SectionScreenProps } from "@/modules/types";
import { RecordDrawer } from "@/components/shell/RecordDrawer";
import { useOpenRecord } from "@/routes/useOpenRecord";

/** `null` renders as "—", never the string "null" — see the field's own doc. */
function ipOrDash(ipAddress: string | null): string {
  return ipAddress ?? "—";
}

function JsonBlock({ label, value }: { label: string; value: unknown }) {
  return (
    <div>
      <div className="text-muted-foreground text-xs">{label}</div>
      {value === null ? (
        <p className="text-muted-foreground text-sm">Nothing recorded.</p>
      ) : (
        <pre className="bg-muted overflow-auto rounded-md p-2 text-xs">
          {JSON.stringify(value, null, 2)}
        </pre>
      )}
    </div>
  );
}

function AuditEntryDetail({
  entry,
  actorName,
  onBack,
}: {
  entry: AuditEntry;
  actorName: string;
  onBack?: () => void;
}) {
  return (
    <div className="flex h-full min-h-0 flex-col">
      {/* Optional: the drawer carries its own close. */}
      {onBack ? (
        <div className="shrink-0 border-b px-4 py-2">
          <Button size="sm" variant="ghost" onClick={onBack}>
            <ArrowLeft />
            Back to log
          </Button>
        </div>
      ) : null}
      <div className="min-h-0 flex-1 space-y-4 overflow-auto p-4">
        <div>
          <h3 className="text-sm font-semibold">
            {auditActionLabel(entry.action)}
          </h3>
          <p className="text-muted-foreground text-sm">
            {actorName} · {new Date(entry.occurredAt).toLocaleString("en-IN")}
          </p>
        </div>

        <dl className="grid grid-cols-[auto_1fr] gap-x-4 gap-y-1 text-sm">
          <dt className="text-muted-foreground">Entity</dt>
          <dd>
            {entry.entityType}
            {entry.entityId ? ` · ${entry.entityId}` : ""}
          </dd>
          <dt className="text-muted-foreground">IP address</dt>
          {/*
           * Set only for sign-ins and permission changes, null everywhere
           * else — deliberately, not a gap (see `AuditEntry.ipAddress`).
           * `ipOrDash` keeps that null from ever rendering as the string
           * "null".
           */}
          <dd className="tabular-nums">{ipOrDash(entry.ipAddress)}</dd>
        </dl>

        <JsonBlock label="Before" value={entry.before} />
        <JsonBlock label="After" value={entry.after} />
      </div>
    </div>
  );
}

/**
 * The audit log (M8-13, FR-ACL-11).
 *
 * Read-only — there is nothing to write here, an audit entry is the record
 * of a write that already happened elsewhere. `actorUserId` is resolved to
 * a name via `renderOverride`, the same pattern `UsersList` uses for role
 * and group ids: the column stores an id so a rename does not retroactively
 * change what a saved view or a filter meant.
 */
export function AuditLog({
  fields,
  filter,
  sort,
  onToggleSort,
  onClearFilter,
}: SectionScreenProps) {
  const data = useDataSource();

  const [limit, setLimit] = useState(25);
  const [history, setHistory] = useCursorHistory();
  const cursor = history[history.length - 1];
  const { recordId, open } = useOpenRecord();

  /*
    Fetched by id rather than found among the rows on screen. Finding it in
    the page could only ever open an entry the current page held; a link is
    followed cold, from a page nobody has loaded (M2-13).
  */
  const entryQuery = useQuery({
    queryKey: ["settings", "audit-entry", recordId],
    queryFn: () =>
      recordId === null ? null : data.settings.getAudit(recordId),
    enabled: recordId !== null,
  });

  const auditQuery = useQuery({
    queryKey: ["settings", "audit", filter, sort, limit, cursor],
    queryFn: () => data.settings.listAudit({ filter, sort, limit, cursor }),
  });

  // Every actor in the log is a user, however long ago they acted — fetched
  // once at a size that covers this fixture's whole roster (six today)
  // rather than per row.
  const usersQuery = useQuery({
    queryKey: ["settings", "users", "all", "for-audit-names"],
    queryFn: () => data.settings.listUsers({ limit: 200 }),
  });

  if (auditQuery.isPending || usersQuery.isPending) return <TableSkeleton />;

  if (auditQuery.isError) {
    return (
      <LoadFailed
        message={auditQuery.error.message}
        onRetry={() => {
          void auditQuery.refetch();
        }}
      />
    );
  }
  if (usersQuery.isError) {
    return (
      <LoadFailed
        message={usersQuery.error.message}
        onRetry={() => {
          void usersQuery.refetch();
        }}
      />
    );
  }

  const actorNames = new Map(
    usersQuery.data.items.map((user) => [user.id, user.name]),
  );
  const nameOf = (actorUserId: string) =>
    actorNames.get(actorUserId) ?? actorUserId;

  return (
    <div className="flex h-full min-h-0 flex-col">
      {recordId !== null ? (
        <RecordDrawer
          title={
            entryQuery.data
              ? auditActionLabel(entryQuery.data.action)
              : "Audit entry"
          }
        >
          {entryQuery.data ? (
            <AuditEntryDetail
              entry={entryQuery.data}
              actorName={nameOf(entryQuery.data.actorUserId)}
            />
          ) : (
            <TableSkeleton rows={3} />
          )}
        </RecordDrawer>
      ) : null}
      <DataTable
        fields={fields}
        rows={auditQuery.data.items}
        getRowId={(entry) => entry.id}
        project={projectAuditEntry}
        sort={sort}
        onToggleSort={onToggleSort}
        onRowClick={(entry) => {
          open(entry.id);
        }}
        renderOverride={(field, entry) => {
          if (field.name === "actorUserId") {
            return renderCell(field, nameOf(entry.actorUserId));
          }
          if (field.name === "ipAddress") {
            return ipOrDash(entry.ipAddress);
          }
          if (field.name === "action") return auditActionLabel(entry.action);
          return undefined;
        }}
        emptyState={<NoMatches onClear={onClearFilter} />}
        footer={
          <Pagination
            page={auditQuery.data}
            limit={limit}
            onLimitChange={(next) => {
              setLimit(next);
              setHistory([undefined]);
            }}
            history={history}
            onNavigate={setHistory}
          />
        }
      />
    </div>
  );
}
